import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, Clock, Star, Sparkles, ListMusic, FileText, Wand2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePlayer, fmtTime } from '@/context/PlayerContext';
import { SongActionsMenu } from '@/components/dialogs';
import { LoadingState, ErrorState, UnauthorizedState } from '@/components/states';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=600';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
}

export default function StudioProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { playTrack, current, playing, toggle } = usePlayer();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
    } catch (e) {
      setError(e?.response?.status === 403 ? 'Ce projet ne t\'appartient pas.' : 'Projet introuvable');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { if (session) load(); }, [session, load]);

  if (!session) return <UnauthorizedState />;
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => navigate('/studio')} />;

  const versions = project.versions || [];
  const main = project.main_song || null;

  const toTrack = (v) => ({
    song_id: v.id, title: v.title, slug: v.slug, cover_url: v.cover_url,
    audio_url: v.audio_url, style_text: v.style_text, duration_seconds: v.duration_seconds,
  });

  const playVersion = (v) => {
    const isCur = current?.song_id === v.id;
    if (isCur) return toggle();
    playTrack(toTrack(v), versions.map(toTrack));
  };

  const setMain = async (v) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/main`, { song_id: v.id });
      setProject(data);
      toast.success(`« ${v.title} » est la version principale.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createVersion = () => {
    const src = main || {};
    const qs = new URLSearchParams();
    if (src.title) qs.set('title', src.title);
    if (src.lyrics) qs.set('lyrics', src.lyrics);
    if (src.style_text) qs.set('style_text', src.style_text);
    if (src.prompt) qs.set('prompt', src.prompt);
    qs.set('project_id', projectId);
    navigate(`/create?${qs.toString()}`);
  };

  return (
    <div className="space-y-8" data-testid="studio-project-page">
      <button onClick={() => navigate('/studio')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white" data-testid="studio-back">
        <ArrowLeft className="h-4 w-4" /> Studio
      </button>

      {/* Project header */}
      <div className="card-surface relative overflow-hidden p-6 sm:p-8">
        <img src={project.cover_url || FALLBACK} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand/20 px-3 py-1 font-mono text-xs uppercase tracking-widest text-brand">Projet Studio</span>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="studio-project-title">{project.title}</h1>
            <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground">
              <span className="text-brand">{project.style_text || 'Style libre'}</span>
              {main?.duration_seconds ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTime(main.duration_seconds)}</span> : null}
              <span className={main?.visibility === 'public' ? 'text-brand' : ''}>{main?.visibility === 'public' ? 'Public' : 'Privé'}</span>
              <span className="flex items-center gap-1"><ListMusic className="h-3 w-3" />{versions.length} version(s)</span>
              <span>Modifié le {fmtDate(project.updated_at)}</span>
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {main && (
              <button onClick={() => playVersion(main)} className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow" data-testid="studio-play-main">
                {current?.song_id === main.id && playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 fill-black" />}
                Lire
              </button>
            )}
            {main && <SongActionsMenu song={main} />}
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Versions */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-white">
              <ListMusic className="h-5 w-5 flex-shrink-0 text-brand" /> Versions du projet
            </h2>
            <button onClick={createVersion} className="flex w-full flex-shrink-0 items-center justify-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-black btn-glow sm:w-auto" data-testid="studio-new-version">
              <Wand2 className="h-4 w-4" /> Créer une nouvelle version
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            Créer une nouvelle version ouvre le moteur de création avec les paramètres préremplis. Tu lances toi-même la génération ; l'original n'est jamais écrasé.
          </p>

          <div className="space-y-2">
            {versions.map((v) => {
              const isCur = current?.song_id === v.id;
              const canPlay = v.generation_status === 'completed';
              return (
                <div key={v.id} className={`card-surface flex items-center gap-3 p-3 ${v.is_main ? 'border-brand/50' : ''}`} data-testid={`version-${v.id}`}>
                  <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg">
                    <img src={v.cover_url || FALLBACK} alt="" className="h-full w-full object-cover" />
                    {canPlay && (
                      <button onClick={() => playVersion(v)} className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100" data-testid={`version-play-${v.id}`}>
                        {isCur && playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 fill-white text-white" />}
                      </button>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-white/5 px-2 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">{v.version_label}</span>
                      {v.is_main && <span className="flex items-center gap-1 rounded-full bg-brand/15 px-2 py-0.5 font-mono text-[10px] uppercase text-brand"><Star className="h-3 w-3 fill-brand" /> Principale</span>}
                    </div>
                    <p className="mt-1 truncate font-display font-semibold text-white">{v.title}</p>
                    <p className="truncate font-mono text-[11px] text-muted-foreground">
                      {fmtDate(v.created_at)}{v.duration_seconds ? ` · ${fmtTime(v.duration_seconds)}` : ''}
                    </p>
                  </div>
                  {!v.is_main && canPlay && (
                    <button onClick={() => setMain(v)} className="flex items-center gap-1 rounded-full bg-surface-card px-3 py-1.5 text-xs font-semibold text-white hover:bg-surface-cardHover" data-testid={`version-set-main-${v.id}`}>
                      <Star className="h-3.5 w-3.5" /> Version principale
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Creation context */}
        <div className="space-y-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-white">
            <FileText className="h-5 w-5 text-brand" /> Contexte de création
          </h2>
          <div className="card-surface space-y-4 p-4 text-sm">
            <Ctx label="Titre" value={main?.title} />
            <Ctx label="Style" value={main?.style_text} />
            <Ctx label="Description / prompt" value={main?.prompt} />
            <Ctx label="Paroles" value={main?.lyrics} pre />
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Génération</p>
              <p className="mt-1 flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-[#22C55E]" />
                {main?.provider ? `Moteur : ${main.provider}` : 'Info moteur indisponible'}
                {main?.duration_seconds ? ` · ${fmtTime(main.duration_seconds)}` : ''}
              </p>
            </div>
          </div>
          <button onClick={createVersion} className="flex w-full items-center justify-center gap-2 rounded-full bg-surface-card py-3 text-sm font-semibold text-white hover:bg-surface-cardHover" data-testid="studio-new-version-2">
            <Sparkles className="h-4 w-4 text-brand" /> Nouvelle version à partir de ce contexte
          </button>
        </div>
      </div>
    </div>
  );
}

function Ctx({ label, value, pre }) {
  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      {value
        ? (pre
            ? <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap font-sans text-white">{value}</pre>
            : <p className="mt-1 text-white">{value}</p>)
        : <p className="mt-1 italic text-muted-foreground">Non renseigné</p>}
    </div>
  );
}
